import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Video,
  VideoOff,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CallPeer, CallSession } from '@/types/call';

interface CallOverlayProps {
  session: CallSession;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  muted: boolean;
  cameraOff: boolean;
  cameraDevice: 'user' | 'environment' | null;
  hasCamera: boolean;
  elapsed: number;
  onAccept: () => void;
  onDecline: () => void;
  onEnd: () => void;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onToggleCameraDevice: () => void;
}

function formatDuration(total: number): string {
  const seconds = Math.max(0, Math.floor(total));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    : `${minutes}:${String(secs).padStart(2, '0')}`;
}

function PeerAvatar({ peer, large = false }: { peer: CallPeer; large?: boolean }) {
  const name = peer.display_name || peer.username || '?';
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10 ring-1 ring-white/20',
        large ? 'h-24 w-24 text-3xl' : 'h-12 w-12 text-lg',
      )}
    >
      {peer.avatar_url ? (
        <img src={peer.avatar_url} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="font-semibold text-white/80">{initial}</span>
      )}
    </div>
  );
}

export default function CallOverlay({
  session,
  localStream,
  remoteStream,
  muted,
  cameraOff,
  cameraDevice,
  hasCamera,
  elapsed,
  onAccept,
  onDecline,
  onEnd,
  onToggleMute,
  onToggleCamera,
  onToggleCameraDevice,
}: CallOverlayProps) {
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);

  const isVideo = session.kind === 'video';
  const hasVideo = !!localStream && localStream.getVideoTracks().length > 0 && !cameraOff;
  const showRemoteVideo =
    isVideo && !!remoteStream && remoteStream.getVideoTracks().length > 0;
  const displayName = session.peer.display_name || session.peer.username || 'Unknown';
  const username = session.peer.username;

  useEffect(() => {
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
  }, [remoteStream, session.status, showRemoteVideo]);

  useEffect(() => {
    if (!showRemoteVideo && remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, showRemoteVideo, session.status]);

  useEffect(() => {
    if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
  }, [localStream, session.status, hasVideo]);

  const statusText =
    session.status === 'outgoing'
      ? 'Calling…'
      : session.status === 'connecting'
        ? 'Connecting…'
        : session.status === 'active'
          ? formatDuration(elapsed)
          : session.status === 'ended'
            ? 'Call ended'
            : session.status === 'error'
              ? session.error || 'Call failed'
              : '';

  if (session.status === 'incoming') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[100] flex flex-col bg-neutral-950 text-white"
        role="dialog"
        aria-modal="true"
        aria-label={`Incoming ${isVideo ? 'video' : 'voice'} call`}
      >
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center pb-[env(safe-area-inset-bottom)]">
          <p className="mb-8 text-xs font-medium uppercase tracking-[0.2em] text-white/50">
            Incoming {isVideo ? 'video' : 'voice'} call
          </p>
          <div className="relative">
            <span className="absolute inset-0 animate-ping rounded-full bg-white/10" />
            <PeerAvatar peer={session.peer} large />
          </div>
          <h2 className="mt-6 max-w-full truncate text-2xl font-semibold">{displayName}</h2>
          {username && <p className="mt-1 text-sm text-white/50">@{username}</p>}

          <div className="mt-12 flex items-center gap-10">
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={onDecline}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                aria-label="Decline call"
              >
                <PhoneOff size={26} />
              </button>
              <span className="text-xs text-white/60">Decline</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={onAccept}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg transition hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                aria-label="Accept call"
              >
                <Phone size={26} />
              </button>
              <span className="text-xs text-white/60">Accept</span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-neutral-950 text-white"
      role="dialog"
      aria-modal="true"
      aria-label={`${isVideo ? 'Video' : 'Voice'} call with ${displayName}`}
    >
      {/* Remote video or audio backdrop */}
      <div className="absolute inset-0">
        {showRemoteVideo ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-800 via-neutral-950 to-black">
            <div className="relative">
              {session.status === 'active' && (
                <>
                  <motion.span
                    animate={{ scale: [1, 1.5], opacity: [0.28, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity }}
                    className="absolute inset-0 rounded-full bg-white/20"
                  />
                  <motion.span
                    animate={{ scale: [1, 1.5], opacity: [0.28, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: 1.2 }}
                    className="absolute inset-0 rounded-full bg-white/20"
                  />
                </>
              )}
              <PeerAvatar peer={session.peer} large />
            </div>
          </div>
        )}
      </div>

      {/* Voice calls, and video calls without a camera track, play through this element */}
      {!showRemoteVideo && <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />}

      {/* Top bar */}
      <div className="relative z-10 flex items-center gap-3 bg-gradient-to-b from-black/70 to-transparent p-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        <PeerAvatar peer={session.peer} />
        <div className="min-w-0">
          <p className="truncate font-semibold">{displayName}</p>
          <p className="truncate text-sm text-white/60">{statusText}</p>
        </div>
      </div>

      {/* Local camera preview */}
      {hasVideo && localStream && session.status !== 'ended' && session.status !== 'error' && (
        <div className="absolute right-4 top-24 z-10 h-36 w-28 overflow-hidden rounded-2xl border border-white/20 bg-black/40 shadow-xl sm:h-44 sm:w-32">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
            style={{
              transform: cameraDevice === 'user' ? 'scaleX(-1)' : 'scaleX(1)',
            }}
          />
          {cameraOff && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white/70">
              <VideoOff size={22} />
            </div>
          )}
        </div>
      )}

      {/* End / error summary */}
      {(session.status === 'ended' || session.status === 'error') && (
        <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
            <Check size={28} />
          </div>
          <p className="mt-4 text-xl font-semibold">
            {session.status === 'ended' ? 'Call ended' : 'Call failed'}
          </p>
          {session.error && (
            <p className="mt-2 max-w-sm text-sm text-white/60">{session.error}</p>
          )}
          {session.status === 'error' && (
            <button
              onClick={onEnd}
              className="mt-6 rounded-full bg-white/10 px-5 py-2 text-sm font-medium transition hover:bg-white/20"
            >
              Close
            </button>
          )}
        </div>
      )}

      {/* Controls */}
      {session.status !== 'ended' && session.status !== 'error' && (
        <div className="relative z-10 mt-auto flex items-center justify-center gap-4 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          <button
            onClick={onToggleMute}
            aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={muted}
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full backdrop-blur transition focus:outline-none focus:ring-2 focus:ring-white/40',
              muted ? 'bg-white text-neutral-900' : 'bg-white/15 text-white hover:bg-white/25',
            )}
          >
            {muted ? <MicOff size={22} /> : <Mic size={22} />}
          </button>

          {hasCamera && (
            <>
              <button
                onClick={onToggleCamera}
                aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}
                aria-pressed={cameraOff}
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-full backdrop-blur transition focus:outline-none focus:ring-2 focus:ring-white/40',
                  cameraOff ? 'bg-white text-neutral-900' : 'bg-white/15 text-white hover:bg-white/25',
                )}
              >
                {cameraOff ? <VideoOff size={22} /> : <Video size={22} />}
              </button>
              <button
                onClick={onToggleCameraDevice}
                aria-label="Flip camera"
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-full backdrop-blur transition focus:outline-none focus:ring-2 focus:ring-white/40',
                  cameraOff ? 'bg-white/15 text-white/50 pointer-events-none' : 'bg-white/15 text-white hover:bg-white/25',
                )}
              >
                <RotateCcw size={20} />
              </button>
            </>
          )}

          <button
            onClick={onEnd}
            aria-label="End call"
            className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
          >
            <PhoneOff size={22} />
          </button>
        </div>
      )}
    </motion.div>
  );
}
